import EICheckBox, { EISelectBox } from 'components/customComponents/Layers/EICheckBox';
import ELayersView from 'enums/ELayersView';
import React, { useState, useEffect, useCallback } from 'react';
import { FmlxButton } from 'fmlx-common-ui';
import { ScreenDTO, ScreenLayerDTO, ScreenLotDTO } from 'business/models/Models';
import ExperimentService from 'business/services/ExperimentService';
import { isNullOrUndefined, showAlertSnackBar } from 'utils/Global';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import { getExpWells } from 'business/plateviewer/Generate/GenerateCommon';
import { updateExperimentLayers } from 'redux/reducers/experimentDataReducer';
import {
  selectExperimentLayers,
  selectSelectedLayer,
} from 'redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import { getUniqueLayerName } from 'business/plateviewer/GenerateUtil';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';

interface EditScreenProps extends WithTabBackButtonStateProps {
  setContentView: (e) => void;
  screen: ScreenDTO;
  selectedLotId: number;
  onScreenLotChange: any;
  setSelectedTab: (val: number) => void;
}

export class LotIdNamePair {
  value: number;
  name: string;
}

let previousLotId = 0;
export function EditScreen(props: EditScreenProps) {
  const {
    setContentView,
    screen,
    selectedLotId,
    onScreenLotChange,
    setHandleOnTabBackPress,
    setSelectedTab,
  } = props;

  if (!isNullOrUndefined(screen) && !isNullOrUndefined(screen.screenLot)) {
    const lot = screen.screenLot.find((sl) => sl.id === selectedLotId);
    if (isNullOrUndefined(lot)) {
      // selectedLotId is not valid, need to set a valid selectedLotId
      const initialLot = screen.screenLot.find((sl) => sl.initialLot === true);
      onScreenLotChange(initialLot.id);
    }
  }
  const dispatch = useRMDispatch();
  const _selectedLayer = useRMSelector(selectSelectedLayer);
  const _layers = useRMSelector(selectExperimentLayers);
  const _setLayers = useCallback(
    (lyrs) => dispatch(updateExperimentLayers(lyrs)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();
  const [refresh, setRefresh] = useState(0);
  const [screenLayer, setScreenLayer] = useState<ScreenLayerDTO>();
  const [isFetchingScreenLot, setIsFetchingScreenLot] = useState(false);
  const [tempScreenLotDetail, setTempScreenLotDetail] = useState<ScreenLotDTO>();
  const screenName = isNullOrUndefined(screen) ? null : screen.name;

  useEffect(() => {
    if (_selectedLayer) {
      setScreenLayer({ ..._selectedLayer.layerDetail } as ScreenLayerDTO);
    }

    setRefresh((refresh) => refresh + 1);
  }, [_selectedLayer]);

  function getScreenLotById(lotId: number): ScreenLotDTO {
    if (isNullOrUndefined(screen) || isNullOrUndefined(screen.screenLot)) {
      return undefined;
    }
    const lot = screen.screenLot.find((sl) => sl.id === lotId);
    return lot;
  }

  function getLotIdNamePair(screenLot: ScreenLotDTO): LotIdNamePair {
    if (isNullOrUndefined(screenLot)) {
      return null;
    }
    const lot = new LotIdNamePair();
    lot.value = screenLot.id;
    lot.name = screenLot.lotNumber.toString();
    return lot;
  }

  function getLotsIdNamePair(): LotIdNamePair[] {
    let lots: LotIdNamePair[] = [];
    if (!isNullOrUndefined(screen) && !isNullOrUndefined(screen.screenLot)) {
      lots = screen.screenLot.map((sl) => getLotIdNamePair(sl));
    }

    return lots;
  }

  useEffect(() => {
    const updateScreenLayerDetails = async (screenLotId: number) => {
      if (!selectedLotId || (tempScreenLotDetail && previousLotId === selectedLotId)) {
        return;
      }
      setIsFetchingScreenLot(true);
      const serv: ExperimentService = new ExperimentService();
      const screenLotDetails: ScreenLotDTO = await serv.getScreenLotDetails(screenLotId);
      setTempScreenLotDetail(screenLotDetails);
      setIsFetchingScreenLot(false);
      previousLotId = selectedLotId;
    };
    // do nothing
    updateScreenLayerDetails(selectedLotId);
  }, [selectedLotId]);

  useEffect(() => {}, [refresh]);

  function getConditionsCount(): number {
    if (
      !isNullOrUndefined(tempScreenLotDetail) &&
      !isNullOrUndefined(tempScreenLotDetail.experiment)
    ) {
      const srcScreenWells = getExpWells(
        tempScreenLotDetail.experiment as ExperimentDtoClient,
      );
      return srcScreenWells.length;
    }
    return 0;
  }

  async function applyScreen() {
    const serv: ExperimentService = new ExperimentService();
    const screenLotDetails: ScreenLotDTO = await serv.getScreenLotDetails(selectedLotId);
    if (!screenLotDetails) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Screen does not exist in database anymore.',
      );

      return;
    }

    if (!isNullOrUndefined(tempScreenLotDetail)) {
      callPreserveUndoableState();
      screenLayer.screenLot = tempScreenLotDetail;
      const index = _layers.findIndex((l) => l.id == _selectedLayer.id);
      const selectedLayerDetail: ScreenLayerDTO = new ScreenLayerDTO();
      selectedLayerDetail.screenLot = screenLayer.screenLot;
      selectedLayerDetail.layerId = screenLayer.layerId;
      selectedLayerDetail.experimentId = screenLayer.experimentId;
      selectedLayerDetail.startingWellNumber = screenLayer.startingWellNumber;
      selectedLayerDetail.volume = screenLayer.volume;
      selectedLayerDetail.id = screenLayer.id;
      if (screen?.name) {
        selectedLayerDetail.screenLot.experiment.expName = screen?.name;
      }
      const tempLayers = cloneDeep(_layers);
      tempLayers[index].dirty = true;
      tempLayers[index].layerDetail = selectedLayerDetail;
      tempLayers[index].name = getUniqueLayerName(
        tempLayers,
        (screen?.name || tempScreenLotDetail.experiment.expName || '') + ' ' + 'Layer',
      );
      _setLayers(tempLayers);
    }

    if (setSelectedTab) {
      setSelectedTab(1);
    }
    setContentView(ELayersView.LAYERDESIGNVIEW);
    setHandleOnTabBackPress(null);
  }

  const getScreenLotId = () => {
    const lot = getScreenLotById(selectedLotId);
    return isNullOrUndefined(lot) ? null : getLotIdNamePair(lot).value;
  };

  function onLotSelectionChange(e) {
    const lotId = parseInt(e.target.value, 10);
    onScreenLotChange(lotId);
  }

  return (
    <div id="commandpanel-screenlayer-edit-wrapper" className="flex flex-col p-6">
      <EICheckBox
        labelText="Layer Name"
        textBoxText={screenName}
        active={screenName === undefined}
        onClick={() => setContentView(ELayersView.SELECTSCREEN)}
      />
      <EISelectBox
        labelText="Lot Number"
        selectedValue={getScreenLotId()}
        onChange={onLotSelectionChange}
        options={getLotsIdNamePair()}
        disabled={false}
      />
      <EICheckBox
        labelText="Conditions"
        textBoxText={getConditionsCount() > 0 ? getConditionsCount() + ' conditions' : ''}
        active={false}
      />
      <div
        id="commandpanel-screenlayer-edit-action-button-wrapper"
        className="mt-[5px] flex justify-end"
      >
        <FmlxButton
          id="commandpanel-screenlayer-edit-apply-button-wrapper"
          disabled={isFetchingScreenLot}
          onClick={applyScreen}
          label="Apply"
          type={FmlxButton.Type.POSITIVE}
        />
      </div>
    </div>
  );
}

export default withTabBackButton(EditScreen);
